import {
  GoogleGenAI,
  Modality,
  type LiveServerMessage,
  type Session,
} from "@google/genai";

const DEFAULT_LIVE_TTS_MODEL = "gemini-2.0-flash-live-preview-04-09";

const getLiveModel = () =>
  import.meta.env.VITE_GEMINI_LIVE_MODEL?.trim() || DEFAULT_LIVE_TTS_MODEL;

type LiveTtsSessionOptions = {
  voiceName?: string;
  languageCode?: string;
  signal?: AbortSignal;
  onMessage: (message: LiveServerMessage) => void;
  onError?: (event: ErrorEvent) => void;
  onClose?: (event: CloseEvent) => void;
};

const getGeminiApiKey = async () => {
  const response = await fetch('/api/gemini-key');
  if (!response.ok) {
    throw new Error("Failed to get key for Gemini Live API.");
  }
  const apiKey = await response.text();
  return apiKey;
};

export async function connectGeminiLiveTtsSession(
  options: LiveTtsSessionOptions
): Promise<Session> {
  const apiKey = await getGeminiApiKey();
  const ai = new GoogleGenAI({ apiKey });
  const session = await ai.live.connect({
    model: getLiveModel(),
    callbacks: {
      onmessage: options.onMessage,
      onerror: (event) => {
        // if (import.meta.env.DEV) {
          console.error("[Live TTS] websocket error", event);
        // }
        options.onError?.(event);
      },
      onclose: (event) => {
        // if (import.meta.env.DEV) {
          console.warn("[Live TTS] websocket closed", event);
        // }
        options.onClose?.(event);
      },
      onopen: () => {
        if (import.meta.env.DEV) {
          console.log("[Live TTS] websocket open");
        }
      },
    },
    config: {
      responseModalities: [Modality.AUDIO],
      speechConfig: {
        languageCode: options.languageCode,
        voiceConfig: {
          prebuiltVoiceConfig: {
            voiceName: options.voiceName,
          },
        },
      },
      abortSignal: options.signal,
    },
  });

  if (options.signal) {
    if (options.signal.aborted) {
      session.close();
    } else {
      options.signal.addEventListener(
        "abort",
        () => {
          session.close();
        },
        { once: true }
      );
    }
  }

  return session;
}
